export type Role = 'user' | 'assistant';

export interface TextBlock {
  type: 'text';
  content: string;
}

export interface TableBlock {
  type: 'table';
  title?: string;
  headers: string[];
  rows: string[][];
}

export interface ChartBlock {
  type: 'chart';
  chartType: 'bar' | 'line';
  title?: string;
  labels: string[];
  datasets: { label: string; data: number[] }[];
}

export interface FlowBlock {
  type: 'flowchart';
  title?: string;
  mermaid: string;
}

export interface BubbleProps {
  msg: Message;
  isStreaming?: boolean;
}

export type Block = TextBlock | TableBlock | ChartBlock | FlowBlock;

export interface Message {
  id: string;
  role: Role;
  blocks: Block[];
  rawText: string;
  toolsUsed?: string[];
  ts: Date;
}

export interface SSEEvent {
  tools?: string[];
  tool_end?: boolean;
  text?: string;
  delta?: string;
  message?: string;
  done?: boolean;
}

// ─── XETGO THEME ──────────────────────────────────────────────────────────────

export const XET_THEME = {
  navy: '#0D1B4B',
  navyLight: '#162259',
  navyMid: '#1E2E6B',
  navyBorder: '#253580',
  yellow: '#FBBC04',
  yellowDark: '#E5A800',
  yellowLight: '#FDD54F',
  white: '#FFFFFF',
  offWhite: '#F7F8FC',
  gray50: '#F0F2F8',
  gray100: '#E2E6F0',
  gray300: '#C0C8DE',
  gray500: '#8892B0',
  gray700: '#4A5578',
  text: '#0D1B4B',
  textMuted: '#5A6480',
  danger: '#E53E3E',
  success: '#06402b',
};

export const SUGGESTED_PROMPTS = [
  'Compare all vendor prices for 500 boxes 30×20×15cm to Pune as a table',
  'Show ongoing RFQ tasks as a table',
  'Show paper rates for all vendors as a bar chart',
  'Show the RFQ pipeline stages as a flowchart',
  'Calculate price for 1000 units 37.5×29.5×24.4cm 180 GSM 2-colour print',
];

export const TOOL_LABELS: Record<string, string> = {
  get_vendors: 'Loading vendors',
  get_paper_rates: 'Fetching paper rates',
  get_order_history: 'Pulling order history',
  calculate_box_price: 'Calculating prices',
  get_rfq_tasks: 'Fetching RFQ tasks',
  get_sampling_tasks: 'Fetching sampling tasks',
  get_rfq_detail: 'Loading RFQ details',
};

export const uid = () => Math.random().toString(36).slice(2, 9);

export const formatTime = (d: Date) =>
  d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

/** Split Claude's raw text into a mixed array of prose + structured blocks */
export function parseBlocks(raw: string): Block[] {
  const blocks: Block[] = [];
  const parts = raw.split(/(```json[\s\S]*?```)/g);
  for (const part of parts) {
    const fence = part.match(/^```json\s*([\s\S]*?)\s*```$/);
    if (fence) {
      try {
        const parsed = JSON.parse(fence[1]);
        if (parsed.type === 'table' && parsed.headers && parsed.rows)
          blocks.push(parsed as TableBlock);
        else if (parsed.type === 'chart' && parsed.labels && parsed.datasets)
          blocks.push(parsed as ChartBlock);
        else if (parsed.type === 'flowchart' && parsed.mermaid)
          blocks.push(parsed as FlowBlock);
        else blocks.push({ type: 'text', content: part });
      } catch {
        blocks.push({ type: 'text', content: part });
      }
    } else if (part.trim()) {
      blocks.push({ type: 'text', content: part });
    }
  }

  return blocks.length ? blocks : [{ type: 'text', content: raw }];
}

/** Download a TableBlock as a CSV file */
export function exportCSV(block: TableBlock) {
  const rows = [block.headers, ...block.rows];
  const csv = rows
    .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(block.title ?? 'export').replace(/\s+/g, '_')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
